import { Button, Card, Modal, Pagination, Space, Table, Tooltip, notification } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatedDate } from 'utils/common';
import PageTitle from '../../components/page-title';
import styles from './style.module.scss';
import CustomButton from 'components/custom-button';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { shortenName } from 'utils/short-name';
import ModalCreateEdit from './component/ModalCreateEdit';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

export interface UserFilter {
  limit: number;
  page: number;
}

const OutStandingManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as UserFilter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);

  const onFinish = (value: Partial<UserFilter>) => {
    setFilter((preState) => ({ ...preState, ...value, page: 1 }));
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.outstanding, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [filter, statusUpdateData]);

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      ellipsis: true,
      key: 'id',
    },
    {
      title: 'Ảnh',
      dataIndex: 'image',
      key: 'image',
      // Ảnh 136px + 2 × 16px đệm ô; không đặt độ rộng thì ảnh tràn sang cột bên cạnh ở màn hẹp.
      width: 168,
      render: (value) => (
        <img
          width={136}
          height={72}
          className={styles.thumb}
          src={`${SERVER_FILE_ENDPOINT}/${value}`}
          alt=""
        />
      ),
    },
    {
      title: 'Liên kết',
      dataIndex: 'link',
      key: 'link',
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 60) || ''}</span>
        </Tooltip>
      ),
    },

    {
      title: 'Ngày',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (value) => <span className={styles.nowrap}>{formatedDate(value)}</span>,
    },
    {
      title: 'Thao tác',
      width: 104,
      key: 'action',
      render: (_, record) => (
        <Tooltip title="Sửa">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handelOpenCreateEditModal(record)}
          />
        </Tooltip>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData(!statusUpdateData);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.outstandingDelete, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
      setSelectedRowKeys([]);
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="Tin nổi bật"
        extra={
          <Space wrap>
            {hasSelected && (
              <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
                Xóa {selectedRowKeys.length} tin
              </Button>
            )}
            <CustomButton
              onClick={() => handelOpenCreateEditModal(valueUserSelected)}
              bgColor="primary-green"
              text="Thêm tin nổi bật"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          scroll={{ x: 600 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateEdit
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa tin nổi bật"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} tin nổi bật đã chọn?</p>
      </Modal>
    </div>
  );
};

export default OutStandingManager;
